/**
 * The transcript, and the one place a turn is taken.
 *
 * Metadata (follow-ups, citations, animation clip) is kept in a side table
 * keyed by message id rather than inside the message, so the transcript stays a
 * plain list that a log view can render without knowing about any of it.
 */
import { Store } from '../core/store.js';
import type { BackendGateway } from './BackendGateway.js';
import type { ConversationState, Message, MessageMeta } from './types.js';

const EMPTY_META: MessageMeta = { followUps: [], citations: [] };

export class ConversationService {
  readonly store = new Store<ConversationState>({
    messages: [],
    meta: {},
    status: 'idle',
  });

  constructor(private readonly gateway: BackendGateway) {}

  getSnapshot(): ConversationState {
    return this.store.getSnapshot();
  }

  subscribe(listener: (state: ConversationState) => void): () => void {
    return this.store.subscribe(listener);
  }

  /** Follow-ups offered against the most recent guide message, if any. */
  currentFollowUps(): string[] {
    const { messages, meta } = this.store.getSnapshot();
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const message = messages[i];
      if (message?.role === 'guide') {
        return meta[message.id]?.followUps ?? [];
      }
    }
    return [];
  }

  /**
   * Seed the guide's opening line without a round trip.
   *
   * This routes through exactly the same path as a real answer, so the reveal,
   * the mouth movement and the follow-up prompts all come along for free — the
   * greeting is not a special case anywhere downstream.
   */
  seedGreeting(text: string, meta: Partial<MessageMeta> = {}): void {
    const message = makeMessage('guide', text);
    const state = this.store.getSnapshot();
    this.store.patch({
      messages: [...state.messages, message],
      meta: { ...state.meta, [message.id]: { ...EMPTY_META, ...meta } },
      status: 'ready',
    });
  }

  /**
   * Take a turn. Appends the user's message immediately so the transcript
   * reflects what was asked even while the guide is thinking, then streams the
   * reply in.
   */
  async ask(text: string): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (this.store.getSnapshot().status === 'sending') return;

    const userMessage = makeMessage('user', trimmed);
    const beforeSend = this.store.getSnapshot();
    this.store.patch({
      messages: [...beforeSend.messages, userMessage],
      status: 'sending',
      error: undefined,
    });

    // The guide's message is appended empty and filled as tokens arrive, so the
    // bubble can appear immediately rather than after the whole reply lands.
    const guideMessage = makeMessage('guide', '');
    let appended = false;

    try {
      const reply = await this.gateway.send(trimmed, {
        onToken: (_chunk, accumulated) => {
          const state = this.store.getSnapshot();
          if (!appended) {
            appended = true;
            this.store.patch({
              messages: [...state.messages, { ...guideMessage, text: accumulated }],
            });
            return;
          }
          this.store.patch({
            messages: state.messages.map((m) =>
              m.id === guideMessage.id ? { ...m, text: accumulated } : m,
            ),
          });
        },
      });

      const state = this.store.getSnapshot();
      const finalMessage: Message = { ...guideMessage, text: reply.message };
      this.store.patch({
        messages: appended
          ? state.messages.map((m) => (m.id === guideMessage.id ? finalMessage : m))
          : [...state.messages, finalMessage],
        meta: {
          ...state.meta,
          [guideMessage.id]: {
            followUps: reply.followUps,
            citations: reply.citations,
            animationHint: reply.animationHint,
            ...(reply.animationClip ? { animationClip: reply.animationClip } : {}),
          },
        },
        status: 'ready',
      });
    } catch (error) {
      // Surfaced as a distinct state, not as a fake guide reply. In a headset a
      // plausible-sounding "sorry, I'm having trouble" is indistinguishable
      // from the guide actually answering, and there is no console to check.
      const state = this.store.getSnapshot();
      this.store.patch({
        // Drop the empty placeholder if tokens never arrived.
        messages: appended
          ? state.messages.filter((m) => m.id !== guideMessage.id || m.text.length > 0)
          : state.messages,
        status: 'error',
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }
}

function makeMessage(role: Message['role'], text: string): Message {
  return { id: crypto.randomUUID(), role, text, at: Date.now() };
}
